import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import GlobeView, { type HoverPosition } from './components/GlobeView';
import LocationPanel from './components/LocationPanel';
import CountryPanel from './components/CountryPanel';
import CountryButton from './components/CountryButton';
import ProfileForm from './components/ProfileForm';
import AuthButton from './components/AuthButton';
import AdminPanel from './components/AdminPanel';
import LeftPanel from './components/LeftPanel';
import Catalogue from './components/Catalogue';
import Leaderboard from './components/Leaderboard';
import BottomNav from './components/BottomNav';
import SuperteamsModal from './components/SuperteamsModal';
import ProfileModal from './components/ProfileModal';
import MobileMenuButton from './components/MobileMenuButton';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import EventsButton from './components/EventsButton';
import EventsPanel from './components/EventsPanel';
import { useAuth } from './hooks/useAuth';
import { useAudioSettings } from './hooks/useAudioSettings';
import { useClickSound } from './hooks/useClickSound';
import { handleAuthCallback, getAuthUrl, joinSuperteam, leaveSuperteam, getCountries, joinCountry, leaveCountry, getGlobalStats, getSuperteams, getMyInvitations } from './utils/api';
import { getFlag } from './utils/flags';
import type { Superteam, Country } from './types';

type MobileTab = 'globe' | 'superteams' | 'projects' | 'events' | 'profile';

// All official Superteam locations with icons and X handles
// Member counts start at 0 - they increase as users register
const SUPERTEAMS: Superteam[] = [
  { id: 'india', name: 'Superteam India', country: 'India', city: 'Bangalore', lat: 12.9716, lng: 77.5946, iconUrl: '/india.jpg', memberCount: 0, activeProjects: 0, twitter: 'SuperteamIN' },
  { id: 'germany', name: 'Superteam Germany', country: 'Germany', city: 'Berlin', lat: 52.5200, lng: 13.4050, iconUrl: '/germany.jpg', memberCount: 0, activeProjects: 0, twitter: 'SuperteamDE' },
  { id: 'nigeria', name: 'Superteam Nigeria', country: 'Nigeria', city: 'Lagos', lat: 6.5244, lng: 3.3792, iconUrl: '/nigeria.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamNG' },
  { id: 'brazil', name: 'Superteam Brazil', country: 'Brazil', city: 'São Paulo', lat: -23.5505, lng: -46.6333, iconUrl: '/brazil.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamBR' },
  { id: 'kazakhstan', name: 'Superteam Kazakhstan', country: 'Kazakhstan', city: 'Almaty', lat: 43.2220, lng: 76.8512, iconUrl: '/kazakhstan.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamKZ' },
  { id: 'malaysia', name: 'Superteam Malaysia', country: 'Malaysia', city: 'Kuala Lumpur', lat: 3.1390, lng: 101.6869, iconUrl: '/malaysia.jpg', memberCount: 0, activeProjects: 0, twitter: 'SuperteamMY' },
  { id: 'balkans', name: 'Superteam Balkan', country: 'Serbia', city: 'Belgrade', lat: 44.7866, lng: 20.4489, iconUrl: '/balkan.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamBLKN' },
  { id: 'korea', name: 'Superteam South Korea', country: 'South Korea', city: 'Seoul', lat: 37.5665, lng: 126.9780, iconUrl: '/korea.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamKorea' },
  { id: 'canada', name: 'Superteam Canada', country: 'Canada', city: 'Toronto', lat: 43.6532, lng: -79.3832, iconUrl: '/canada.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamCAN' },
  { id: 'singapore', name: 'Superteam Singapore', country: 'Singapore', city: 'Singapore', lat: 1.3521, lng: 103.8198, iconUrl: '/singapore.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamSG' },
  { id: 'poland', name: 'Superteam Poland', country: 'Poland', city: 'Warsaw', lat: 52.2297, lng: 21.0122, iconUrl: '/poland.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamPOL' },
  { id: 'indonesia', name: 'Superteam Indonesia', country: 'Indonesia', city: 'Jakarta', lat: -6.2088, lng: 106.8456, iconUrl: '/indonesia.jpeg', memberCount: 0, activeProjects: 0, twitter: 'SuperteamINDO' },
  { id: 'netherlands', name: 'Superteam Netherlands', country: 'Netherlands', city: 'Amsterdam', lat: 52.3676, lng: 4.9041, iconUrl: '/netherlands.jpg', memberCount: 0, activeProjects: 0 },
  { id: 'japan', name: 'Superteam Japan', country: 'Japan', city: 'Tokyo', lat: 35.6762, lng: 139.6503, iconUrl: '/japan.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamJapan' },
  { id: 'uk', name: 'Superteam UK', country: 'United Kingdom', city: 'London', lat: 51.5074, lng: -0.1278, iconUrl: '/uk.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamUK' },
  { id: 'uae', name: 'Superteam UAE', country: 'UAE', city: 'Dubai', lat: 25.2048, lng: 55.2708, iconUrl: '/uae.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamAE' },
  { id: 'georgia', name: 'Superteam Georgia', country: 'Georgia', city: 'Tbilisi', lat: 41.7151, lng: 44.8271, iconUrl: '/georgia.jpg', memberCount: 0, activeProjects: 0, twitter: 'SuperteamGEO' },
  { id: 'ireland', name: 'Superteam Ireland', country: 'Ireland', city: 'Dublin', lat: 53.3498, lng: -6.2603, iconUrl: '/ireland.jpg', memberCount: 0, activeProjects: 0, twitter: 'superteamIE' },
  { id: 'spain', name: 'Superteam Spain', country: 'Spain', city: 'Madrid', lat: 40.4168, lng: -3.7038, iconUrl: '/spain.jpg', memberCount: 0, activeProjects: 0, twitter: 'LaFamilia_so' },
  { id: 'ukraine', name: 'Superteam Ukraine', country: 'Ukraine', city: 'Kyiv', lat: 50.4501, lng: 30.5234, iconUrl: '/ukraine.jpg', memberCount: 0, activeProjects: 0 },
  { id: 'australia', name: 'Superteam Australia', country: 'Australia', city: 'Sydney', lat: -33.8688, lng: 151.2093, iconUrl: '/australia.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamAU' },
  { id: 'usa', name: 'Superteam USA', country: 'United States', city: 'New York', lat: 40.7128, lng: -74.0060, iconUrl: '/usa.png', memberCount: 0, activeProjects: 0, twitter: 'SuperteamUSA' },
];

function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      // If this is a popup for verification, notify opener and close
      if (window.opener) {
        window.opener.postMessage({ type: 'x-verification-callback', error: errorParam }, window.location.origin);
        window.close();
        return;
      }
      setError('Authentication was cancelled or failed');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    if (code && state) {
      // Check if this is a popup window (used for team/project X verification)
      // If so, post message back to opener instead of processing as login
      if (window.opener) {
        window.opener.postMessage({ type: 'x-verification-callback', code, state }, window.location.origin);
        window.close();
        return;
      }

      // Regular login flow
      handleAuthCallback(code, state)
        .then(({ user, token }) => {
          login(user, token);
          navigate('/');
        })
        .catch((err) => {
          console.error('Auth callback error:', err);
          setError('Failed to complete authentication');
          setTimeout(() => navigate('/'), 3000);
        });
    } else {
      navigate('/');
    }
  }, [searchParams, login, navigate]);

  const containerStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: '#030014',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ color: '#f87171', marginBottom: '8px' }}>{error}</p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center' }}>
        <div 
          className="animate-spin"
          style={{ 
            width: '48px', 
            height: '48px', 
            border: '2px solid rgba(20,241,149,0.3)', 
            borderTopColor: '#14F195', 
            borderRadius: '50%',
            margin: '0 auto 16px',
          }} 
        />
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Completing sign in...</p>
      </div>
    </div>
  );
}

function GlobalStats({ superteams, totalBuilders, isMobile }: { superteams: Superteam[]; totalBuilders: number; isMobile: boolean }) {
  const stats = useMemo(() => ({
    totalMembers: superteams.reduce((sum, t) => sum + t.memberCount, 0),
    totalTeams: superteams.length,
  }), [superteams]);

  const statCardStyle: CSSProperties = {
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: isMobile ? '8px' : '12px',
    padding: isMobile ? '8px 12px' : '12px 16px',
    textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
      <div style={statCardStyle}>
        <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: 'white' }}>{stats.totalTeams}</div>
        <div style={{ fontSize: isMobile ? '8px' : '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teams</div>
      </div>
      <div style={statCardStyle}>
        <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: '#9945FF' }}>{stats.totalMembers.toLocaleString()}</div>
        <div style={{ fontSize: isMobile ? '8px' : '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Members</div>
      </div>
        <div style={statCardStyle}>
        <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: '#14F195' }}>{totalBuilders.toLocaleString()}</div>
        <div style={{ fontSize: isMobile ? '8px' : '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Builders</div>
        </div>
    </div>
  );
}

function HomePage() {
  const { isAuthenticated, isLoading, user, updateUser } = useAuth();
  const audioSettings = useAudioSettings();
  const { withClickSound } = useClickSound();
  const queryClient = useQueryClient();
  const [selectedSuperteam, setSelectedSuperteam] = useState<Superteam | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [hoveredSuperteam, setHoveredSuperteam] = useState<Superteam | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<Country | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [countryJoinLoading, setCountryJoinLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAudioDropdown, setShowAudioDropdown] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>('globe');
  const [showEventsDesktop, setShowEventsDesktop] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden
  );
  const audioDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch countries data
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  const countries = countriesData?.countries || [];

  // Fetch superteams data (with real member counts from database)
  const { data: superteamsData } = useQuery({
    queryKey: ['superteams'],
    queryFn: getSuperteams,
    staleTime: 60 * 1000, // 1 minute
  });

  // Merge API data with static SUPERTEAMS (API provides member counts, static has Twitter handles, icons)
  const superteams = useMemo(() => {
    if (!superteamsData?.superteams) return SUPERTEAMS;
    
    return SUPERTEAMS.map(staticTeam => {
      const apiTeam = superteamsData.superteams.find(t => t.id === staticTeam.id);
      return {
        ...staticTeam,
        memberCount: apiTeam?.memberCount ?? staticTeam.memberCount,
        activeProjects: apiTeam?.activeProjects ?? staticTeam.activeProjects,
      };
    });
  }, [superteamsData]);

  // Fetch user's pending invitations
  const { data: invitationsData } = useQuery({
    queryKey: ['myInvitations'],
    queryFn: getMyInvitations,
    enabled: isAuthenticated && isPageVisible,
    refetchInterval: isPageVisible ? 120000 : false, // Poll less aggressively while page is visible
  });

  const pendingInvitations = invitationsData?.data?.invitations?.filter(inv => inv.status === 'pending') || [];
  const invitationCount = pendingInvitations.length;

  // Fetch global stats (builders count)
  const { data: statsData } = useQuery({
    queryKey: ['globalStats'],
    queryFn: getGlobalStats,
    staleTime: 60 * 1000, // 1 minute
  });
  const totalBuilders = statsData?.data?.totalBuilders || 0;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Close audio dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (audioDropdownRef.current && !audioDropdownRef.current.contains(event.target as Node)) {
        setShowAudioDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignIn = useCallback(async () => {
    try {
      setAuthLoading(true);
      const { url } = await getAuthUrl();
      // Use TWA-aware OAuth opening to keep flow within TWA
      const { openOAuthUrl } = await import('./utils/twa');
      openOAuthUrl(url);
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      setAuthLoading(false);
    }
  }, []);

  const handleSuperteamClick = useCallback((superteam: Superteam) => {
    if (isMobile) {
      // On mobile, switch to superteams tab and select the superteam
      setActiveMobileTab('superteams');
      // The SuperteamsModal will handle showing the LocationPanel
    } else {
      // Desktop behavior - show LocationPanel directly
    setSelectedSuperteam(superteam);
    setSelectedCountry(null);
    setShowProfile(false);
    }
  }, [isMobile]);

  const handleSuperteamHover = useCallback((superteam: Superteam | null, position?: HoverPosition) => {
    setHoveredSuperteam(superteam);
    setHoveredCountry(null);
    setHoverPosition(position || null);
  }, []);

  const handleCountryClick = useCallback((country: Country) => {
    setSelectedCountry(country);
    setSelectedSuperteam(null);
    setShowProfile(false);
  }, []);

  const handleCountryHover = useCallback((country: Country | null, position?: HoverPosition) => {
    setHoveredCountry(country);
    setHoveredSuperteam(null);
    setHoverPosition(position || null);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedSuperteam(null);
    setSelectedCountry(null);
  }, []);

  const handleOpenProfile = useCallback(() => {
    setShowProfile(true);
    setSelectedSuperteam(null);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setShowProfile(false);
  }, []);

  const handleJoinSuperteam = useCallback(async (superteamId: string) => {
    if (!isAuthenticated) {
      // Redirect to sign in
      handleSignIn();
      return;
    }
    
    try {
      setJoinLoading(true);
      const response = await joinSuperteam(superteamId);
      if (response.success && response.data?.user) {
        updateUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to join superteam:', error);
    } finally {
      setJoinLoading(false);
    }
  }, [isAuthenticated, handleSignIn, updateUser]);

  const handleLeaveSuperteam = useCallback(async () => {
    try {
      setJoinLoading(true);
      const response = await leaveSuperteam();
      if (response.success && response.data?.user) {
        updateUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to leave superteam:', error);
    } finally {
      setJoinLoading(false);
    }
  }, [updateUser]);

  const handleJoinCountry = useCallback(async (countryCode: string) => {
    if (!isAuthenticated) {
      // Redirect to sign in
      handleSignIn();
      return;
    }
    
    try {
      setCountryJoinLoading(true);
      const response = await joinCountry(countryCode);
      if (response.success && response.data?.user) {
        updateUser(response.data.user);
        // Immediately refetch countries data to update map with new marker
        await queryClient.refetchQueries({ queryKey: ['countries'] });
      }
    } catch (error) {
      console.error('Failed to join country:', error);
    } finally {
      setCountryJoinLoading(false);
    }
  }, [isAuthenticated, handleSignIn, updateUser, queryClient]);

  const handleLeaveCountry = useCallback(async () => {
    try {
      setCountryJoinLoading(true);
      const response = await leaveCountry();
      if (response.success && response.data?.user) {
        updateUser(response.data.user);
        // Immediately refetch countries data to update map
        await queryClient.refetchQueries({ queryKey: ['countries'] });
      }
    } catch (error) {
      console.error('Failed to leave country:', error);
    } finally {
      setCountryJoinLoading(false);
    }
  }, [updateUser, queryClient]);

  // Responsive styles
  // On mobile, hide header/footer when a modal is open (not on globe tab)
  const shouldShowHeaderFooter = !isMobile || activeMobileTab === 'globe';
  
  const headerStyle: CSSProperties = {
    position: 'fixed',
    top: isMobile ? 0 : 18,
    left: isMobile ? 0 : '50%',
    right: isMobile ? 0 : 'auto',
    transform: isMobile ? 'none' : 'translateX(-50%)',
    width: isMobile ? '100%' : 'min(1180px, calc(100vw - 36px))',
    zIndex: 50,
    background: 'rgba(10, 10, 20, 0.82)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.12)',
    borderBottom: isMobile ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: isMobile ? 0 : '20px',
    boxShadow: isMobile ? 'none' : '0 12px 40px rgba(0,0,0,0.35)',
    opacity: shouldShowHeaderFooter ? 1 : 0,
    pointerEvents: shouldShowHeaderFooter ? 'auto' : 'none',
    transition: 'opacity 0.3s ease',
  };

  const headerContentStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'space-between' : 'center',
    gap: isMobile ? 0 : '28px',
    padding: isMobile ? '10px 16px' : '18px 24px',
  };

  const footerStyle: CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    background: 'rgba(10, 10, 20, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    opacity: shouldShowHeaderFooter ? 1 : 0,
    pointerEvents: shouldShowHeaderFooter ? 'auto' : 'none',
    transition: 'opacity 0.3s ease',
  };

  const footerContentStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isMobile ? '10px 16px' : '12px 24px',
  };

  // 🧙‍♂️ MYTHICAL TOLY EASTER EGG - Super rare event!
  // 1/1000 chance (0.1%) to see the Mythical Toly appear in space
  const [showMythicalToly, setShowMythicalToly] = useState(false);
  const [tolyFading, setTolyFading] = useState(false);
  const tolyTimeoutRef = useRef<{ fade?: ReturnType<typeof setTimeout>; hide?: ReturnType<typeof setTimeout> }>({});
  
  // Function to trigger Toly appearance (used by random chance and dev button)
  const triggerMythicalToly = useCallback(() => {
    // Clear any existing timeouts
    if (tolyTimeoutRef.current.fade) clearTimeout(tolyTimeoutRef.current.fade);
    if (tolyTimeoutRef.current.hide) clearTimeout(tolyTimeoutRef.current.hide);
    
    console.log('🧙‍♂️ THE MYTHICAL TOLY HAS APPEARED! You are truly blessed.');
    setShowMythicalToly(true);
    setTolyFading(false);
    
    // Start fading out after 8 seconds
    tolyTimeoutRef.current.fade = setTimeout(() => {
      setTolyFading(true);
    }, 8000);
    
    // Fully disappear after 10 seconds
    tolyTimeoutRef.current.hide = setTimeout(() => {
      setShowMythicalToly(false);
      setTolyFading(false);
    }, 10000);
  }, []);
  
  useEffect(() => {
    // Roll the dice on page load - 1 in 1000 chance
    const roll = Math.random();
    const isLucky = roll < 0.001; // 0.1% chance
    
    if (isLucky) {
      triggerMythicalToly();
    }
    
    return () => {
      if (tolyTimeoutRef.current.fade) clearTimeout(tolyTimeoutRef.current.fade);
      if (tolyTimeoutRef.current.hide) clearTimeout(tolyTimeoutRef.current.hide);
    };
  }, [triggerMythicalToly]);

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: '#030014', zIndex: 0 }}>
      
      {/* 🧙‍♂️ MYTHICAL TOLY - The rarest of rare Easter eggs */}
      {showMythicalToly && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: isMobile ? '-10%' : '-5%', // Slightly off the right
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            zIndex: 5, // Above globe background but below UI
            pointerEvents: 'none',
            opacity: tolyFading ? 0 : 0.12, // 12% opacity for visibility
            transition: 'opacity 2s ease-out',
          }}
        >
          <img
            src="/toly.png"
            alt="The Mythical Toly"
            style={{
              height: '90vh',
              width: 'auto',
              objectFit: 'contain',
              filter: 'grayscale(20%) brightness(1.1) drop-shadow(0 0 100px rgba(153, 69, 255, 0.3))',
              animation: 'float-toly 6s ease-in-out infinite',
            }}
          />
        </div>
      )}
      
      {/* Globe - Always visible underneath */}
      <div style={{ position: 'relative', width: '100vw', height: '100vh', zIndex: 1 }}>
        <GlobeView
          superteams={superteams}
          countries={countries}
          selectedSuperteam={!isMobile ? selectedSuperteam : null}
          selectedCountry={!isMobile ? selectedCountry : null}
          onSuperteamClick={handleSuperteamClick}
          onSuperteamHover={handleSuperteamHover}
          onCountryClick={handleCountryClick}
          onCountryHover={handleCountryHover}
        />
      </div>

      {/* Header */}
      <header style={headerStyle}>
        <div style={headerContentStyle}>
          {isMobile ? (
            // Mobile: Just logo and menu button
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img 
                  src="/branding/logosymbol.png" 
                  alt="Superwhere"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    objectFit: 'cover',
                  }}
                />
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'white' }}>Superwhere</div>
              </div>
              <MobileMenuButton
                onProfileClick={handleOpenProfile}
                onAdminClick={user?.isAdmin ? () => setShowAdmin(true) : undefined}
                onCountryChange={() => queryClient.invalidateQueries({ queryKey: ['countries'] })}
                onLeaderboardClick={() => setShowLeaderboard(true)}
                invitationCount={invitationCount}
              />
            </>
          ) : (
            // Desktop: centered larger header
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: '280px' }}>
                <img 
                  src="/branding/logosymbol.png" 
                  alt="Superwhere"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    objectFit: 'cover',
                  }}
                />
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: 'white', lineHeight: 1.1 }}>Superwhere</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '1.6px' }}>
                    Global Builder & Superteam Directory
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {/* Audio Controls */}
                <div ref={audioDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={withClickSound(() => setShowAudioDropdown(!showAudioDropdown))}
                style={{
                  padding: isMobile ? '8px' : '12px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: audioSettings.muted ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                {audioSettings.muted ? (
                  <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
                )}
              </button>

              {/* Audio Dropdown */}
              {showAudioDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'rgba(10, 10, 20, 0.95)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    minWidth: '200px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                    zIndex: 100,
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>Audio Settings</span>
                      <button
                        onClick={withClickSound(() => audioSettings.toggleMute())}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          background: audioSettings.muted ? 'rgba(239, 68, 68, 0.2)' : 'rgba(20, 241, 149, 0.2)',
                          border: `1px solid ${audioSettings.muted ? 'rgba(239, 68, 68, 0.3)' : 'rgba(20, 241, 149, 0.3)'}`,
                          color: audioSettings.muted ? '#ef4444' : '#14F195',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {audioSettings.muted ? 'Unmute' : 'Mute All'}
                      </button>
                    </div>
                  </div>

                  {/* Sound Effects Volume */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.7)' }} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                        </svg>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Sound Effects</span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{Math.round(audioSettings.soundEffectsVolume * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={audioSettings.soundEffectsVolume}
                      onChange={(e) => audioSettings.setSoundEffectsVolume(parseFloat(e.target.value))}
                      disabled={audioSettings.muted}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        appearance: 'none',
                        cursor: audioSettings.muted ? 'not-allowed' : 'pointer',
                        opacity: audioSettings.muted ? 0.5 : 1,
                        background: `linear-gradient(to right, #14F195 0%, #14F195 ${audioSettings.soundEffectsVolume * 100}%, rgba(255,255,255,0.1) ${audioSettings.soundEffectsVolume * 100}%, rgba(255,255,255,0.1) 100%)`,
                      }}
                    />
                  </div>

                  <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '12px', textAlign: 'center' }}>
                    Click sounds on buttons
                  </p>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />

                  {/* Leaderboard Button */}
                  <button
                    onClick={withClickSound(() => {
                      setShowLeaderboard(true);
                      setShowAudioDropdown(false);
                    })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(153, 69, 255, 0.1))',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      color: '#FFD700',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>🏆</span>
                    Leaderboard
                  </button>
                </div>
              )}
            </div>

            {/* Events Button */}
            {!isMobile && <EventsButton onOpen={() => setShowEventsDesktop(true)} />}

            {/* Projects Hub Button */}
            <button
              onClick={withClickSound(() => setShowLeftPanel(true))}
              style={{
                padding: isMobile ? '8px 12px' : '12px 18px',
                borderRadius: '14px',
                border: '1px solid rgba(20, 241, 149, 0.3)',
                background: 'linear-gradient(135deg, rgba(20, 241, 149, 0.2), rgba(153, 69, 255, 0.2))',
                color: '#14F195',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(20, 241, 149, 0.3), rgba(153, 69, 255, 0.3))';
                e.currentTarget.style.borderColor = 'rgba(20, 241, 149, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(20, 241, 149, 0.2), rgba(153, 69, 255, 0.2))';
                e.currentTarget.style.borderColor = 'rgba(20, 241, 149, 0.3)';
              }}
              title={invitationCount > 0 ? `Open Projects Hub (${invitationCount} pending invitation${invitationCount !== 1 ? 's' : ''})` : "Open Projects Hub"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              {!isMobile && 'Projects'}
              {invitationCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: '#14F195',
                  color: 'black',
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: '10px',
                  minWidth: '18px',
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(20, 241, 149, 0.4)',
                }}>
                  {invitationCount > 9 ? '9+' : invitationCount}
                </span>
              )}
            </button>

                <CountryButton onCountryChange={() => queryClient.invalidateQueries({ queryKey: ['countries'] })} />

                <AuthButton 
                  onProfileClick={handleOpenProfile} 
                  onAdminClick={() => setShowAdmin(true)}
                  onLeaderboardClick={() => setShowLeaderboard(true)}
                />
              </div>
            </>
          )}
        </div>
      </header>

      {/* Footer - Desktop only */}
      {!isMobile && (
        <div style={footerStyle}>
          <div style={footerContentStyle}>
            {/* Stats */}
            <GlobalStats superteams={superteams} totalBuilders={totalBuilders} isMobile={isMobile} />
            
            {/* Powered by */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              <a 
                href="https://x.com/Belacosaursol" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
              >
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>Powered by</span>
                <span className="text-solana-gradient" style={{ fontSize: '12px', fontWeight: 600 }}>Belacosaursol</span>
              </a>
              <a 
                href="https://x.com/SuperteamINDO" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
              >
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>Proud member of</span>
                <img 
                  src="/indonesia.jpeg" 
                  alt="Superteam Indonesia"
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    borderRadius: '4px',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    // Fallback to flag emoji if image fails to load
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent && !parent.querySelector('.flag-fallback')) {
                      const fallback = document.createElement('span');
                      fallback.className = 'flag-fallback';
                      fallback.textContent = '🇮🇩';
                      fallback.style.fontSize = '14px';
                      e.currentTarget.parentElement?.insertBefore(fallback, e.currentTarget.nextSibling);
                    }
                  }}
                />
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Superteam Indonesia</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hover Tooltip for Superteam - Desktop only */}
      {!isMobile && hoveredSuperteam && !selectedSuperteam && !selectedCountry && hoverPosition && (
        <div 
          className="animate-fade-in"
          style={{
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 30,
            left: hoverPosition.x,
            top: hoverPosition.y - 10,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div style={{
            background: 'rgba(10, 10, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '12px 16px',
            minWidth: '200px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              {hoveredSuperteam.iconUrl && (
                <img 
                  src={hoveredSuperteam.iconUrl} 
                  alt={hoveredSuperteam.name}
                  style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              )}
              <div>
                <p style={{ fontWeight: 600, color: 'white', fontSize: '14px' }}>{hoveredSuperteam.name}</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{hoveredSuperteam.city}, {hoveredSuperteam.country}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>👥 {hoveredSuperteam.memberCount}</span>
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>🚀 {hoveredSuperteam.activeProjects} projects</span>
            </div>
          </div>
        </div>
      )}

      {/* Hover Tooltip for Country - Desktop only */}
      {!isMobile && hoveredCountry && !selectedSuperteam && !selectedCountry && hoverPosition && (
        <div 
          className="animate-fade-in"
          style={{
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 30,
            left: hoverPosition.x,
            top: hoverPosition.y - 10,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div style={{
            background: 'rgba(10, 10, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '12px 16px',
            minWidth: '180px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ fontSize: '28px' }}>{getFlag(hoveredCountry.flagEmoji, hoveredCountry.code)}</div>
              <div>
                <p style={{ fontWeight: 600, color: 'white', fontSize: '14px' }}>{hoveredCountry.name}</p>
                <p style={{ fontSize: '11px', color: 'rgba(153, 69, 255, 0.8)' }}>Community Representation</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>👥 {hoveredCountry.memberCount} representing</span>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Overlay */}
      {!isLoading && !isAuthenticated && !selectedSuperteam && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 20,
          padding: isMobile ? '16px' : '24px',
        }}>
          <div 
            className="animate-slide-up"
            style={{
              pointerEvents: 'auto',
              width: '100%',
              maxWidth: isMobile ? '320px' : '380px',
            }}
          >
            <div style={{
              background: 'rgba(10, 10, 20, 0.9)',
              backdropFilter: 'blur(30px)',
              WebkitBackdropFilter: 'blur(30px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: isMobile ? '16px' : '20px',
              padding: isMobile ? '24px 20px' : '32px 28px',
              textAlign: 'center',
            }}>
              <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>
                Find Your <span className="text-solana-gradient">Superteam</span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: isMobile ? '14px' : '15px', marginBottom: isMobile ? '20px' : '28px' }}>
                Connect with Solana builders worldwide
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button 
                  onClick={withClickSound(handleSignIn)}
                  disabled={authLoading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: isMobile ? '14px 20px' : '16px 24px',
                    borderRadius: isMobile ? '12px' : '14px',
                    fontWeight: 600,
                    fontSize: isMobile ? '15px' : '16px',
                    background: 'linear-gradient(to right, #14F195, #0da672)',
                    color: 'black',
                    border: 'none',
                    cursor: 'pointer',
                    opacity: authLoading ? 0.5 : 1,
                  }}
                >
                  {authLoading ? (
                    <div className="animate-spin" style={{ width: '20px', height: '20px', border: '2px solid rgba(0,0,0,0.3)', borderTopColor: 'black', borderRadius: '50%' }} />
                  ) : (
                    <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )}
                  Sign in with X
                </button>
                
                <button 
                  onClick={withClickSound(() => {
                    const firstTeam = superteams[0];
                    if (firstTeam) setSelectedSuperteam(firstTeam);
                  })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '14px 20px' : '16px 24px',
                    borderRadius: isMobile ? '12px' : '14px',
                    fontWeight: 500,
                    fontSize: isMobile ? '15px' : '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Explore Teams
        </button>
              </div>
              
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: isMobile ? '11px' : '13px', marginTop: isMobile ? '16px' : '24px' }}>
                {isMobile ? 'Tap any marker to learn more' : 'Click any marker to learn more'}
        </p>
      </div>
          </div>
        </div>
      )}

      {/* Location Panel */}
      <LocationPanel
        superteam={selectedSuperteam}
        superteams={superteams}
        isOpen={!!selectedSuperteam}
        onClose={handleClosePanel}
        onNavigate={handleSuperteamClick}
        user={user}
        isAuthenticated={isAuthenticated}
        onJoin={handleJoinSuperteam}
        onLeave={handleLeaveSuperteam}
        joinLoading={joinLoading}
      />

      {/* Country Panel */}
      <CountryPanel
        country={selectedCountry}
        isOpen={!!selectedCountry}
        onClose={handleClosePanel}
        user={user}
        isAuthenticated={isAuthenticated}
        onJoin={handleJoinCountry}
        onLeave={handleLeaveCountry}
        joinLoading={countryJoinLoading}
      />

      {/* Profile Form */}
      {isAuthenticated && (
        <ProfileForm
          isOpen={showProfile}
          onClose={handleCloseProfile}
          superteams={superteams}
          countries={countries}
        />
      )}

      {/* Admin Panel */}
      {user?.isAdmin && (
        <AdminPanel
          isOpen={showAdmin}
          onClose={() => setShowAdmin(false)}
          superteams={superteams}
        />
      )}

      {/* Left Panel - Projects Hub */}
      <LeftPanel
        isOpen={showLeftPanel}
        onClose={() => setShowLeftPanel(false)}
      />

      {/* Open Data Catalogue */}
      {showCatalogue && (
        <Catalogue onClose={() => setShowCatalogue(false)} />
      )}

      {/* Leaderboard */}
      <Leaderboard isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Mobile Bottom Navigation (hidden while Events is open for full-screen modal) */}
      {!(isMobile && activeMobileTab === 'events') && (
        <BottomNav
          activeTab={activeMobileTab}
          onTabChange={(tab) => {
            setActiveMobileTab(tab);
            // Close any open panels when switching tabs
            if (tab !== 'globe') {
              setSelectedSuperteam(null);
              setSelectedCountry(null);
              setShowProfile(false);
            }
          }}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Mobile Modals */}
      {isMobile && (
        <>
          <SuperteamsModal
            isOpen={activeMobileTab === 'superteams'}
            onClose={() => setActiveMobileTab('globe')}
            superteams={superteams}
            onSuperteamClick={(superteam) => {
              setSelectedSuperteam(superteam);
            }}
          />

          <LeftPanel
            isOpen={activeMobileTab === 'projects'}
            onClose={() => setActiveMobileTab('globe')}
          />

          <ProfileModal
            isOpen={activeMobileTab === 'profile'}
            onClose={() => setActiveMobileTab('globe')}
            superteams={superteams}
            countries={countries}
          />

          <EventsPanel
            isOpen={activeMobileTab === 'events'}
            onClose={() => setActiveMobileTab('globe')}
            isMobile
          />
        </>
      )}

      {/* Desktop Events Panel */}
      {!isMobile && (
        <EventsPanel
          isOpen={showEventsDesktop}
          onClose={() => setShowEventsDesktop(false)}
          isMobile={false}
        />
      )}

      {/* LocationPanel for mobile (nested in SuperteamsModal) */}
      {isMobile && selectedSuperteam && (
        <LocationPanel
          superteam={selectedSuperteam}
          superteams={superteams}
          isOpen={true}
          onClose={() => setSelectedSuperteam(null)}
          onNavigate={(superteam) => {
            setSelectedSuperteam(superteam);
          }}
          user={user}
          isAuthenticated={isAuthenticated}
          onJoin={async (superteamId: string) => {
            if (!isAuthenticated) {
              handleSignIn();
              return;
            }
            try {
              setJoinLoading(true);
              const response = await joinSuperteam(superteamId);
              if (response.success && response.data?.user) {
                updateUser(response.data.user);
              }
            } catch (error) {
              console.error('Failed to join superteam:', error);
            } finally {
              setJoinLoading(false);
            }
          }}
          onLeave={async () => {
            try {
              setJoinLoading(true);
              const response = await leaveSuperteam();
              if (response.success && response.data?.user) {
                updateUser(response.data.user);
              }
            } catch (error) {
              console.error('Failed to leave superteam:', error);
            } finally {
              setJoinLoading(false);
            }
          }}
          joinLoading={joinLoading}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
    </Routes>
  );
}
